import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../api';
import { useToast } from '../ToastContext';
import {
  computeLandedCostINR,
  computeThreeScenarios,
  computeRecommendedQuoteTwoLayer,
  reverseMarginSplit,
} from '../lib/landedCost';

// Default validUntil = today + 30 days
function defaultValidUntil() {
  const d = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}

const MODES = [
  { value: 'DirectEXW', label: 'Direct / EXW' },
  { value: 'CNERZY_Margin', label: 'CNERZY Margin' },
  { value: 'DutyPaidAV', label: 'Duty Paid AV (India BCD+SWS+IGST)' },
  { value: 'ExWorksINR', label: 'Ex-Works INR' },
  { value: 'LandedCostGlobal', label: 'Landed Cost Global (CIF)' },
];

const LANDED_MODES = new Set(['DutyPaidAV', 'ExWorksINR', 'LandedCostGlobal']);

function fmt2(n) {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// fmtInr kept as utility for future use
// eslint-disable-next-line no-unused-vars
function fmtInr(n) {
  if (!Number.isFinite(n)) return '—';
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function CustomerQuote({ rfqId, rfq, rfqLines = [] }) {
  const toast = useToast();

  // ── Basic state ────────────────────────────────────────────────────
  const [mode, setMode] = useState(rfq?.quoteMode || 'DirectEXW');
  const [validUntil, setValidUntil] = useState(defaultValidUntil());
  const [hideSupplierTraces, setHideSupplierTraces] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedQuote, setSavedQuote] = useState(null);
  // D3-UI: detect if any underlying QuoteLine has been revised since the saved quote
  const [quoteIsStale, setQuoteIsStale] = useState(false);

  // Lines scope: 'all' | 'decided' | 'custom'
  const [linesScope, setLinesScope] = useState('decided');
  const [customLineIds, setCustomLineIds] = useState(new Set());

  // Currency toggle: 'INR' | 'USD'
  const [currency, setCurrency] = useState('INR');

  // ── Inputs panel (landed-cost modes) ───────────────────────────────
  const [inputsOpen, setInputsOpen] = useState(true);
  const [customsFx, setCustomsFx] = useState(92);
  const [salesFx, setSalesFx] = useState(92);
  const [bcdPct, setBcdPct] = useState(16);
  const [swsPct, setSwsPct] = useState(10);
  const [igstPct, setIgstPct] = useState(18);
  const [minPct, setMinPct] = useState(40);
  const [targetPct, setTargetPct] = useState(55);
  const [stretchPct, setStretchPct] = useState(60);
  // Two-layer margins for LandedCostGlobal
  const [coreMarginPct, setCoreMarginPct] = useState(10);
  const [miscMarginPct, setMiscMarginPct] = useState(3);
  // Reverse margin
  const [customerSellingPrice, setCustomerSellingPrice] = useState('');

  // D3-UI: Detect stale customer quote (any line's revision changed after saved quote was created)
  useEffect(() => {
    if (!savedQuote || !rfqLines.length) { setQuoteIsStale(false); return; }
    // Check if any envelope for this RFQ has been revised since the saved quote was created
    // Heuristic: if currentRevision > 1, mark as potentially stale
    const checkStale = async () => {
      try {
        const envResp = await api.listEnvelopes(rfqId);
        const envelopes = envResp.items || [];
        for (const env of envelopes) {
          if ((env.currentRevision || 1) > 1) {
            setQuoteIsStale(true);
            return;
          }
        }
        setQuoteIsStale(false);
      } catch (_) { /* swallow */ }
    };
    checkStale();
  }, [savedQuote, rfqLines.length, rfqId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tenant defaults
  useEffect(() => {
    api.getTenant().then((t) => {
      if (t?.defaultTaxRates) {
        if (t.defaultTaxRates.bcdPct != null) setBcdPct(t.defaultTaxRates.bcdPct);
        if (t.defaultTaxRates.swsPct != null) setSwsPct(t.defaultTaxRates.swsPct);
        if (t.defaultTaxRates.igstPct != null) setIgstPct(t.defaultTaxRates.igstPct);
      }
      if (t?.defaultFx) {
        if (t.defaultFx.customsFx != null) setCustomsFx(t.defaultFx.customsFx);
        if (t.defaultFx.salesFx != null) setSalesFx(t.defaultFx.salesFx);
      }
    }).catch(() => {}); // swallow error if endpoint not available yet
  }, []);

  // ── Per-line input overrides ───────────────────────────────────────
  // lineInputs: { [lineId]: { unitCostUSD, freightUSD, insuranceUSD, additionsUSD, miscINR } }
  const [lineInputs, setLineInputs] = useState({});
  function setLineInput(lineId, field, value) {
    setLineInputs((prev) => ({
      ...prev,
      [lineId]: { ...prev[lineId], [field]: value },
    }));
  }

  // Legacy margin overrides (for DirectEXW / CNERZY_Margin modes)
  const [defaultMargin, setDefaultMargin] = useState(12);
  const [lineOverrides, setLineOverrides] = useState([]);
  function updateLineOverride(rfqLineItemId, margin) {
    setLineOverrides((prev) => {
      const next = prev.filter((o) => o.rfqLineItemId !== rfqLineItemId);
      next.push({ rfqLineItemId, margin: Number(margin) });
      return next;
    });
  }

  // ── Derived lines for display ──────────────────────────────────────
  const decidedLines = rfqLines.filter((l) => ['Decided', 'CustomerQuoted', 'Won', 'Lost'].includes(l.lineStatus));

  const visibleLines = useMemo(() => {
    if (linesScope === 'all') return rfqLines;
    if (linesScope === 'decided') return decidedLines;
    return rfqLines.filter((l) => customLineIds.has(String(l._id)));
  }, [linesScope, rfqLines, decidedLines, customLineIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Live derivations ───────────────────────────────────────────────
  const derivedRows = useMemo(() => {
    if (!LANDED_MODES.has(mode)) return [];
    const cifMode = mode === 'LandedCostGlobal';
    return visibleLines.map((line) => {
      const inp = lineInputs[String(line._id)] || {};
      const unitCostUSD = Number(inp.unitCostUSD) || (line.unitPrice ? Number(line.unitPrice) : 0);
      const freightUSD = Number(inp.freightUSD) || 0;
      const insuranceUSD = Number(inp.insuranceUSD) || 0;
      const additionsUSD = Number(inp.additionsUSD) || 0;
      const miscINR = Number(inp.miscINR) || 0;
      const qty = line.quantity || 1;

      const landed = computeLandedCostINR({
        unitCostUSD,
        qty,
        customsFx,
        freightUSD,
        insuranceUSD,
        additionsUSD,
        miscINR,
        bcdPct,
        swsPct,
        igstPct,
        cifFreightInAssessable: cifMode,
      });

      const scenarios = computeThreeScenarios({
        landedUnit: landed.landedUnitINR,
        _landedUnitRaw: landed._landedUnitRaw,
        qty,
        minPct,
        targetPct,
        stretchPct,
      });

      return { line, unitCostUSD, landed, scenarios };
    });
  }, [mode, visibleLines, lineInputs, customsFx, bcdPct, swsPct, igstPct, minPct, targetPct, stretchPct]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Summary totals ─────────────────────────────────────────────────
  const summaryTotals = useMemo(() => {
    if (!derivedRows.length) return null;
    const minTotal = derivedRows.reduce((s, r) => s + (r.scenarios.minLineTotal || 0), 0);
    const targetTotal = derivedRows.reduce((s, r) => s + (r.scenarios.targetLineTotal || 0), 0);
    const stretchTotal = derivedRows.reduce((s, r) => s + (r.scenarios.stretchLineTotal || 0), 0);
    const landedTotal = derivedRows.reduce((s, r) => s + (r.landed.landedTotalINR || 0), 0);
    return { minTotal, targetTotal, stretchTotal, landedTotal };
  }, [derivedRows]);

  // ── Reverse margin for LandedCostGlobal ───────────────────────────
  const reverseResult = useMemo(() => {
    if (mode !== 'LandedCostGlobal' || !summaryTotals || !customerSellingPrice) return null;
    const sp = Number(customerSellingPrice);
    if (!Number.isFinite(sp) || sp <= 0) return null;
    return reverseMarginSplit({
      sellingPrice: sp,
      landedCost: summaryTotals.landedTotal,
      coreMarginPct,
      miscMarginPct,
    });
  }, [mode, summaryTotals, customerSellingPrice, coreMarginPct, miscMarginPct]);

  // ── Two-layer recommended quote ───────────────────────────────────
  const recommendedQuote = useMemo(() => {
    if (mode !== 'LandedCostGlobal' || !summaryTotals) return null;
    return computeRecommendedQuoteTwoLayer({
      landedCost: summaryTotals.landedTotal,
      coreMarginPct,
      miscMarginPct,
    });
  }, [mode, summaryTotals, coreMarginPct, miscMarginPct]);

  // ── Get selected lineItemIds ───────────────────────────────────────
  function getSelectedLineIds() {
    if (linesScope === 'all') return [];
    if (linesScope === 'decided') {
      const ids = decidedLines.map((l) => String(l._id));
      return ids.length > 0 ? ids : [];
    }
    return [...customLineIds];
  }

  // ── Save handler ───────────────────────────────────────────────────
  async function save() {
    setBusy(true);
    try {
      const lineItemIds = getSelectedLineIds();
      const body = {
        mode,
        defaultMargin,
        validUntil: validUntil || undefined,
        hideSupplierTraces,
      };
      if (lineItemIds.length > 0) body.lineItemIds = lineItemIds;
      if (mode === 'CNERZY_Margin' && lineOverrides.length > 0) body.lineOverrides = lineOverrides;

      // Include landed-cost inputs if relevant
      if (LANDED_MODES.has(mode)) {
        body.customsFxRate = customsFx;
        body.salesFxRate = salesFx;
        body.bcdPct = bcdPct;
        body.swsPct = swsPct;
        body.igstPct = igstPct;
        body.coreMarginPct = coreMarginPct;
        body.miscMarginPct = miscMarginPct;
      }

      const q = await api.createCustomerQuote(rfqId, body);
      if (q.lines && (mode === 'DirectEXW' || mode === 'CNERZY_Margin')) {
        setLineOverrides(q.lines.map((l) => ({
          rfqLineItemId: String(l.rfqLineItemId),
          margin: l.margin ?? defaultMargin,
        })));
      }
      setSavedQuote(q);
      setQuoteIsStale(false);
      toast.push('Customer quote saved');
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  const isLandedMode = LANDED_MODES.has(mode);
  const hasLines = visibleLines.length > 0;

  return (
    <div>
      <h2 className="card-title">Customer quote</h2>

      {/* D3-UI: Stale customer quote warning */}
      {quoteIsStale && (
        <div className="banner review" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>⚠</span>
          <div style={{ flex: 1 }}>
            <b>Underlying quote(s) have been revised</b> since this customer quote was saved. Prices may be stale.
          </div>
          <button
            className="btn btn-sm"
            onClick={save}
            disabled={busy}
          >
            {busy ? 'Refreshing…' : 'Refresh from latest revision'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => setQuoteIsStale(false)}>Dismiss</button>
        </div>
      )}

      {/* ── Mode + basic settings ──────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="row" style={{ flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <label className="field">
            <span className="label">Quote mode</span>
            <select className="select" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="quote-mode">
              {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </label>
          {(mode === 'CNERZY_Margin') && (
            <label className="field">
              <span className="label">Default margin (%)</span>
              <input className="input mono" type="number" value={defaultMargin} onChange={(e) => setDefaultMargin(Number(e.target.value))} />
            </label>
          )}
          <label className="field">
            <span className="label">Valid until</span>
            <input className="input" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
          </label>
          <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={hideSupplierTraces} onChange={(e) => setHideSupplierTraces(e.target.checked)} />
            <span className="label" style={{ margin: 0 }}>Hide supplier traces</span>
          </label>
        </div>
      </div>

      {/* ── Lines to include ──────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 10 }}>Lines to include</div>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          {[
            ['all', `All lines (${rfqLines.length})`],
            ['decided', `Decided lines only (${decidedLines.length})`],
            ['custom', 'Selected manually'],
          ].map(([v, label]) => (
            <label key={v} style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" name="linesScope" value={v} checked={linesScope === v} onChange={() => setLinesScope(v)} />
              <span>{label}</span>
              {v === 'decided' && decidedLines.length < rfqLines.length && linesScope === 'decided' && (
                <span className="chip review" style={{ fontSize: 11 }}>
                  {rfqLines.length - decidedLines.length} pending
                </span>
              )}
            </label>
          ))}
        </div>
        {linesScope === 'custom' && rfqLines.length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {rfqLines.map((l) => (
              <span
                key={l._id}
                className={'chip ' + (customLineIds.has(String(l._id)) ? 'primary' : 'muted')}
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setCustomLineIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(String(l._id))) next.delete(String(l._id));
                  else next.add(String(l._id));
                  return next;
                })}
              >
                #{l.lineNumber} {l.partNumber}
                {customLineIds.has(String(l._id)) && <span style={{ marginLeft: 4 }}>✓</span>}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Inputs panel (landed-cost modes only) ──────────────────── */}
      {isLandedMode && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
            onClick={() => setInputsOpen((o) => !o)}
          >
            <h2 className="card-title" style={{ margin: 0, flex: 1 }}>Inputs &amp; rates {inputsOpen ? '▾' : '▸'}</h2>
          </div>
          {inputsOpen && (
            <div style={{ marginTop: 12 }}>
              <div className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
                <label className="field">
                  <span className="label">Customs FX (INR/USD)</span>
                  <input className="input mono" type="number" step="0.01" value={customsFx} onChange={(e) => setCustomsFx(Number(e.target.value))} data-testid="input-customsFx" />
                </label>
                <label className="field">
                  <span className="label">Sales FX (INR/USD)</span>
                  <input className="input mono" type="number" step="0.01" value={salesFx} onChange={(e) => setSalesFx(Number(e.target.value))} data-testid="input-salesFx" />
                </label>
                <label className="field">
                  <span className="label">BCD %</span>
                  <input className="input mono" type="number" step="0.01" value={bcdPct} onChange={(e) => setBcdPct(Number(e.target.value))} data-testid="input-bcdPct" />
                </label>
                <label className="field">
                  <span className="label">SWS %</span>
                  <input className="input mono" type="number" step="0.01" value={swsPct} onChange={(e) => setSwsPct(Number(e.target.value))} data-testid="input-swsPct" />
                </label>
                <label className="field">
                  <span className="label">IGST %</span>
                  <input className="input mono" type="number" step="0.01" value={igstPct} onChange={(e) => setIgstPct(Number(e.target.value))} data-testid="input-igstPct" />
                </label>
              </div>
              {mode !== 'LandedCostGlobal' && (
                <div className="row" style={{ flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
                  <label className="field">
                    <span className="label">Min margin %</span>
                    <input className="input mono" type="number" step="0.1" value={minPct} onChange={(e) => setMinPct(Number(e.target.value))} data-testid="input-minPct" />
                  </label>
                  <label className="field">
                    <span className="label">Target margin %</span>
                    <input className="input mono" type="number" step="0.1" value={targetPct} onChange={(e) => setTargetPct(Number(e.target.value))} data-testid="input-targetPct" />
                  </label>
                  <label className="field">
                    <span className="label">Stretch margin %</span>
                    <input className="input mono" type="number" step="0.1" value={stretchPct} onChange={(e) => setStretchPct(Number(e.target.value))} data-testid="input-stretchPct" />
                  </label>
                </div>
              )}
              {mode === 'LandedCostGlobal' && (
                <div className="row" style={{ flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
                  <label className="field">
                    <span className="label">Core margin %</span>
                    <input className="input mono" type="number" step="0.1" value={coreMarginPct} onChange={(e) => setCoreMarginPct(Number(e.target.value))} data-testid="input-coreMarginPct" />
                  </label>
                  <label className="field">
                    <span className="label">Misc margin %</span>
                    <input className="input mono" type="number" step="0.1" value={miscMarginPct} onChange={(e) => setMiscMarginPct(Number(e.target.value))} data-testid="input-miscMarginPct" />
                  </label>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Per-line table (landed-cost modes) ─────────────────────── */}
      {isLandedMode && hasLines && (
        <div className="card" style={{ marginBottom: 16, overflowX: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <h2 className="card-title" style={{ margin: 0, flex: 1 }}>Line build-up</h2>
            <div style={{ display: 'flex', gap: 6 }}>
              {['INR', 'USD'].map((c) => (
                <span key={c} className={'chip ' + (currency === c ? 'primary' : 'muted')} style={{ cursor: 'pointer' }} onClick={() => setCurrency(c)} data-testid={`currency-${c}`}>{c}</span>
              ))}
            </div>
          </div>
          <table className="table" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th>Line</th>
                <th>Part</th>
                <th>Qty</th>
                {/* Input columns */}
                <th>Unit USD</th>
                {mode !== 'ExWorksINR' && <th>Freight $</th>}
                {mode !== 'ExWorksINR' && <th>Insurance $</th>}
                {mode !== 'LandedCostGlobal' && <th>Additions $</th>}
                <th>Misc INR</th>
                {/* Derived columns */}
                <th className="mono">Assessable</th>
                <th className="mono">BCD</th>
                <th className="mono">SWS</th>
                <th className="mono">IGST</th>
                <th className="mono">Landed Unit</th>
                <th className="mono" style={{ background: 'var(--surface-2)' }}>Sell M1</th>
                <th className="mono" style={{ background: 'var(--surface-2)' }}>Sell M2</th>
                <th className="mono" style={{ background: 'var(--surface-2)' }}>Sell M3</th>
                <th className="mono" style={{ background: 'var(--success-tint)' }}>Total M1</th>
                <th className="mono" style={{ background: 'var(--success-tint)' }}>Total M2</th>
                <th className="mono" style={{ background: 'var(--success-tint)' }}>Total M3</th>
              </tr>
            </thead>
            <tbody>
              {derivedRows.map(({ line, unitCostUSD, landed, scenarios }) => {
                const inp = lineInputs[String(line._id)] || {};
                const toDisplay = (inr) => currency === 'USD' ? fmt2(inr / salesFx) : fmt2(inr);
                const prefix = currency === 'USD' ? '$' : '₹';
                return (
                  <tr key={line._id}>
                    <td>{line.lineNumber}</td>
                    <td className="mono">{line.partNumber}</td>
                    <td className="mono">{line.quantity}</td>
                    {/* Input cells */}
                    <td>
                      <input className="input mono" type="number" step="0.01" style={{ width: 75, padding: '2px 4px', fontSize: 11 }}
                        value={inp.unitCostUSD !== undefined ? inp.unitCostUSD : (line.unitPrice || '')}
                        onChange={(e) => setLineInput(String(line._id), 'unitCostUSD', e.target.value)}
                        placeholder="0.00" />
                    </td>
                    {mode !== 'ExWorksINR' && (
                      <td><input className="input mono" type="number" step="0.01" style={{ width: 70, padding: '2px 4px', fontSize: 11 }}
                        value={inp.freightUSD || ''} onChange={(e) => setLineInput(String(line._id), 'freightUSD', e.target.value)} placeholder="0" /></td>
                    )}
                    {mode !== 'ExWorksINR' && (
                      <td><input className="input mono" type="number" step="0.01" style={{ width: 70, padding: '2px 4px', fontSize: 11 }}
                        value={inp.insuranceUSD || ''} onChange={(e) => setLineInput(String(line._id), 'insuranceUSD', e.target.value)} placeholder="0" /></td>
                    )}
                    {mode !== 'LandedCostGlobal' && (
                      <td><input className="input mono" type="number" step="0.01" style={{ width: 70, padding: '2px 4px', fontSize: 11 }}
                        value={inp.additionsUSD || ''} onChange={(e) => setLineInput(String(line._id), 'additionsUSD', e.target.value)} placeholder="0" /></td>
                    )}
                    <td><input className="input mono" type="number" step="1" style={{ width: 70, padding: '2px 4px', fontSize: 11 }}
                      value={inp.miscINR || ''} onChange={(e) => setLineInput(String(line._id), 'miscINR', e.target.value)} placeholder="0" /></td>
                    {/* Derived read-only cells */}
                    <td className="mono" style={{ color: 'var(--ink-500)' }}>{prefix}{toDisplay(landed.assessableINR)}</td>
                    <td className="mono" style={{ color: 'var(--ink-500)' }}>{prefix}{toDisplay(landed.bcdINR)}</td>
                    <td className="mono" style={{ color: 'var(--ink-500)' }}>{prefix}{toDisplay(landed.swsINR)}</td>
                    <td className="mono" style={{ color: 'var(--ink-500)' }}>{prefix}{toDisplay(landed.igstINR)}</td>
                    <td className="mono" style={{ fontWeight: 600 }}>{prefix}{toDisplay(landed.landedUnitINR)}</td>
                    <td className="mono" style={{ background: 'var(--surface-2)' }}>{prefix}{toDisplay(scenarios.minSellUnit)}</td>
                    <td className="mono" style={{ background: 'var(--surface-2)' }}>{prefix}{toDisplay(scenarios.targetSellUnit)}</td>
                    <td className="mono" style={{ background: 'var(--surface-2)' }}>{prefix}{toDisplay(scenarios.stretchSellUnit)}</td>
                    <td className="mono" style={{ background: 'var(--success-tint)', fontWeight: 700 }} data-testid="line-total-m1">{prefix}{toDisplay(scenarios.minLineTotal)}</td>
                    <td className="mono" style={{ background: 'var(--success-tint)', fontWeight: 700 }} data-testid="line-total-m2">{prefix}{toDisplay(scenarios.targetLineTotal)}</td>
                    <td className="mono" style={{ background: 'var(--success-tint)', fontWeight: 700 }} data-testid="line-total-m3">{prefix}{toDisplay(scenarios.stretchLineTotal)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Summary block ─────────────────────────────────────────────── */}
      {isLandedMode && summaryTotals && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <h2 className="card-title" style={{ margin: 0, flex: 1 }}>Summary</h2>
            <div style={{ display: 'flex', gap: 6 }}>
              {['INR', 'USD'].map((c) => (
                <span key={c} className={'chip ' + (currency === c ? 'primary' : 'muted')} style={{ cursor: 'pointer' }} onClick={() => setCurrency(c)}>{c}</span>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {[
              { label: 'Landed cost', value: summaryTotals.landedTotal, testid: 'summary-landed' },
              { label: `Quote Total M#1 (${minPct}%)`, value: summaryTotals.minTotal, testid: 'summary-m1' },
              { label: `Quote Total M#2 (${targetPct}%)`, value: summaryTotals.targetTotal, testid: 'summary-m2' },
              { label: `Quote Total M#3 (${stretchPct}%)`, value: summaryTotals.stretchTotal, testid: 'summary-m3' },
            ].map(({ label, value, testid }) => {
              const disp = currency === 'USD' ? value / salesFx : value;
              const prefix = currency === 'USD' ? '$' : '₹';
              return (
                <div key={label} className="card section-bg" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 11, color: 'var(--ink-500)', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontWeight: 700, fontSize: 16 }} data-testid={testid}>
                    {prefix}{fmt2(disp)}
                  </div>
                </div>
              );
            })}
          </div>
          {mode === 'LandedCostGlobal' && recommendedQuote != null && (
            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>Recommended quote (two-layer):</span>
              <span style={{ fontWeight: 700, fontSize: 15 }} data-testid="recommended-quote">
                {currency === 'USD' ? '$' + fmt2(recommendedQuote / salesFx) : '₹' + fmt2(recommendedQuote)}
              </span>
              <span className="chip info" style={{ fontSize: 11 }}>Core {coreMarginPct}% + Misc {miscMarginPct}%</span>
            </div>
          )}
        </div>
      )}

      {/* ── Reverse-margin panel (LandedCostGlobal only) ────────────── */}
      {mode === 'LandedCostGlobal' && summaryTotals && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h2 className="card-title">Reverse-margin check</h2>
          <div className="row" style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label className="field">
              <span className="label">Customer final selling price ({currency})</span>
              <input
                className="input mono"
                type="number"
                step="1"
                value={customerSellingPrice}
                onChange={(e) => setCustomerSellingPrice(e.target.value)}
                placeholder="Enter customer price…"
                data-testid="input-customerSellingPrice"
                style={{ width: 180 }}
              />
            </label>
            {reverseResult && (
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>Total margin</div>
                  <div style={{ fontWeight: 700 }} data-testid="reverse-total-margin">
                    {currency === 'USD' ? '$' + fmt2(reverseResult.totalMargin / salesFx) : '₹' + fmt2(reverseResult.totalMargin)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>Core margin earned</div>
                  <div style={{ fontWeight: 600 }} data-testid="reverse-core-margin">
                    {currency === 'USD' ? '$' + fmt2(reverseResult.coreMarginEarned / salesFx) : '₹' + fmt2(reverseResult.coreMarginEarned)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--ink-500)' }}>Misc margin earned</div>
                  <div style={{ fontWeight: 600 }} data-testid="reverse-misc-margin">
                    {currency === 'USD' ? '$' + fmt2(reverseResult.miscMarginEarned / salesFx) : '₹' + fmt2(reverseResult.miscMarginEarned)}
                  </div>
                </div>
                <span
                  className={'chip ' + (reverseResult.isFeasible ? 'ready' : 'risk')}
                  data-testid="feasibility-chip"
                >
                  {reverseResult.isFeasible ? '✓ Feasible' : `✗ Below target (${reverseResult.deltaPct}%)`}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Legacy table (DirectEXW / CNERZY_Margin) + saved quote ─── */}
      {savedQuote && !isLandedMode && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <b>Mode: {savedQuote.mode}</b>
            {savedQuote.validUntil && (
              <span style={{ marginLeft: 16, fontSize: 13, color: '#6B7280' }}>
                Valid until {new Date(savedQuote.validUntil).toLocaleDateString()}
              </span>
            )}
            {savedQuote.lineItemIds && savedQuote.lineItemIds.length > 0 && (
              <span className="chip info" style={{ marginLeft: 12, fontSize: 11 }}>
                {savedQuote.lineItemIds.length} of {rfqLines.length} lines
              </span>
            )}
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <ExportButton id={savedQuote._id} format="csv" filename={`customer-quote-${savedQuote._id}.csv`}>Export CSV</ExportButton>
              <ExportButton id={savedQuote._id} format="xlsx" filename={`customer-quote-${savedQuote._id}.xlsx`} testId="export-xlsx">Export Excel</ExportButton>
              <ExportButton id={savedQuote._id} format="pdf" filename={`customer-quote-${savedQuote._id}.html`}>Open PDF</ExportButton>
            </div>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Part</th><th>Qty</th><th>Cost</th>
                {mode === 'CNERZY_Margin' && <th>Margin %</th>}
                <th>Final</th><th>Currency</th><th>Lead</th><th>Condition</th>
              </tr>
            </thead>
            <tbody>
              {savedQuote.lines.map((l, i) => {
                const lineId = String(l.rfqLineItemId);
                const overrideEntry = lineOverrides.find((o) => o.rfqLineItemId === lineId);
                const marginVal = overrideEntry != null ? overrideEntry.margin : (l.margin ?? defaultMargin);
                return (
                  <tr key={i}>
                    <td className="mono">{l.partNumber}</td>
                    <td className="mono">{l.qty}</td>
                    <td className="mono">{Number(l.unitPrice).toFixed(2)}</td>
                    {mode === 'CNERZY_Margin' && (
                      <td className="mono">
                        <input className="input mono" type="number" style={{ width: 70, padding: '2px 4px' }}
                          value={marginVal} onChange={(e) => updateLineOverride(lineId, e.target.value)} />
                      </td>
                    )}
                    <td className="mono"><b>{Number(l.finalUnitPrice).toFixed(2)}</b></td>
                    <td>{l.currency}</td>
                    <td>{l.leadTime}</td>
                    <td>{l.condition}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {savedQuote.total != null && (
            <div style={{ textAlign: 'right', fontWeight: 700, fontSize: 15, marginTop: 8 }}>
              Total: {savedQuote.lines?.[0]?.currency || 'USD'} {Number(savedQuote.total).toFixed(2)}
            </div>
          )}
          {mode === 'CNERZY_Margin' && lineOverrides.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <button className="btn btn-secondary btn-sm" onClick={save} disabled={busy}>
                {busy ? 'Rebuilding…' : 'Rebuild with per-line margins'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Save + Export actions ──────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className="btn btn-accent"
          onClick={save}
          disabled={busy || (linesScope === 'custom' && customLineIds.size === 0)}
          data-testid="btn-save"
        >
          {busy ? 'Saving…' : 'Save quote'}
        </button>
        {savedQuote && (
          <>
            <ExportButton id={savedQuote._id} format="csv" filename={`customer-quote-${savedQuote._id}.csv`}>Export CSV</ExportButton>
            <ExportButton id={savedQuote._id} format="xlsx" filename={`customer-quote-${savedQuote._id}.xlsx`} testId="export-xlsx">Export Excel</ExportButton>
            <ExportButton id={savedQuote._id} format="pdf" filename={`customer-quote-${savedQuote._id}.html`}>Open PDF</ExportButton>
          </>
        )}
      </div>
    </div>
  );
}

// Authenticated export button — hits the API with Bearer header and triggers a
// browser download (or opens HTML in a new tab). Plain <a href> would be sent
// without a JWT and the backend rejects with 401.
function ExportButton({ id, format, filename, testId, children }) {
  const toast = useToast();
  const [busy, setBusy] = React.useState(false);
  return (
    <button
      type="button"
      className="btn btn-secondary btn-sm"
      disabled={busy}
      data-testid={testId}
      onClick={async () => {
        setBusy(true);
        try {
          await api.download(api.exportUrl(id, format), filename);
        } catch (e) {
          toast.push(e.message || 'Export failed', { variant: 'error' });
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? 'Preparing…' : children}
    </button>
  );
}
